import React from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import AdminPanel from './AdminPanel';
import MercadoPagoConfigModal from './MercadoPagoConfigModal';
import ModalPoliticaDescuentos from './ModalPoliticaDescuentos';
import TiendaDisenoModal from './TiendaDisenoModal';
import { authService } from '../api/auth.js';
import { obtenerNotificacionesPagos, marcarNotificacionPagoLeida, obtenerNotificaciones, marcarNotificacionLeida, activarUsuario } from '../api/backend_v2';
import { tieneAccesoModulo } from '../utils/modulosSistema';
import {
  ChartBarSquareIcon,
  UsersIcon,
  BanknotesIcon,
  CreditCardIcon,
  PlusCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  SparklesIcon,
  BeakerIcon,
  InboxArrowDownIcon,
  ArchiveBoxIcon,
  ChatBubbleLeftRightIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  ArrowLeftStartOnRectangleIcon,
  UserPlusIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  GlobeAltIcon,
  TagIcon,
  RocketLaunchIcon,
  ChevronDownIcon,
  UserCircleIcon,
  SwatchIcon
} from '@heroicons/react/24/outline';
import { BellIcon } from '@heroicons/react/24/solid';
import { isDemoUser, abrirTourDemo, solicitarRegistroDemo } from '../utils/demoMode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const resolveLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return url;
};

function getRolBadgeClass(rol) {
  if (rol === 'superadmin') return 'bg-slate-900 text-white';
  if (rol === 'admin') return 'bg-red-100 text-red-800';
  if (rol === 'gerente') return 'bg-purple-100 text-purple-800';
  return 'bg-blue-100 text-blue-800';
}

function formatRolLabel(rol) {
  return rol ? rol.charAt(0).toUpperCase() + rol.slice(1) : '';
}

const Navigation = ({ currentView, onViewChange, usuario, modulosHabilitados, onLogout, logoConfig, logoScale, onLogoUpdated }) => {
  const esSuperAdmin = usuario?.rol === 'superadmin';
  const esOperario = usuario?.rol === 'operario';
  const esAdminOGerente = usuario?.rol === 'admin' || usuario?.rol === 'gerente' || esSuperAdmin;
  const hasModulo = React.useCallback(
    (moduloCodigo) => tieneAccesoModulo(modulosHabilitados, moduloCodigo, esSuperAdmin),
    [modulosHabilitados, esSuperAdmin]
  );
  const [logoAspectRatio, setLogoAspectRatio] = React.useState(1);

  const logoFrameStyle = React.useMemo(() => {
    // Base visual for logo; grows with configured scale.
    const scaledHeight = Math.round(48 * (Number(logoScale) || 1));
    const maxHeightPx = Math.max(32, Math.min(84, scaledHeight));
    const minWidthPx = 48;
    const maxWidthPx = 260;
    const computedWidth = Math.round(maxHeightPx * logoAspectRatio);
    const safeWidth = Math.max(minWidthPx, Math.min(maxWidthPx, computedWidth));

    return {
      width: `${safeWidth}px`,
      height: `${maxHeightPx}px`,
    };
  }, [logoAspectRatio, logoScale]);

  const obtenerInicialesOptica = React.useCallback((nombreOptica) => {
    const nombre = (nombreOptica || '').trim();
    if (!nombre) return 'OP';
    const partes = nombre.split(/\s+/).filter(Boolean);
    if (partes.length === 1) {
      return partes[0].slice(0, 2).toUpperCase();
    }
    return (partes[0][0] + partes[1][0]).toUpperCase();
  }, []);

  React.useEffect(() => {
    const handleLogoUpdated = () => {
      // Trigger parent to reload the branding data
      if (typeof onLogoUpdated === 'function') {
        onLogoUpdated();
      }
    };

    window.addEventListener('optica-logo-updated', handleLogoUpdated);
    return () => window.removeEventListener('optica-logo-updated', handleLogoUpdated);
  }, [onLogoUpdated]);

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: ChartBarSquareIcon, modulo: 'dashboard' },
    { key: 'clientes', label: 'Clientes', icon: UsersIcon, modulo: 'clientes' },
    { key: 'lista-precios', label: 'Lista de Precios', icon: ClipboardDocumentListIcon, modulo: 'lista_precios' },
    { key: 'nuevo-cliente', label: 'Nuevo Cliente', icon: PlusCircleIcon, modulo: 'nuevo_cliente' },
  ];
  const navItemsVisibles = navItems.filter((item) => hasModulo(item.modulo));

  const finanzasItems = [
    { key: 'finanzas', label: 'Finanzas', icon: BanknotesIcon, modulo: 'finanzas' },
    { key: 'facturacion', label: 'Facturación', icon: DocumentTextIcon, modulo: 'facturacion' },
    { key: 'balance', label: 'Balance', icon: ChartBarIcon, modulo: 'balance' },
  ];
  const finanzasItemsVisibles = finanzasItems.filter((item) => hasModulo(item.modulo));

  const anteojosEstados = [
    { key: 'anteojos-todos', label: 'Todos', icon: EyeIcon },
    { key: 'anteojos-tomados', label: 'Tomados', icon: PencilSquareIcon },
    { key: 'anteojos-preparados', label: 'Preparados', icon: SparklesIcon },
    { key: 'anteojos-laboratorio', label: 'En laboratorio', icon: BeakerIcon },
    { key: 'anteojos-para-retirar', label: 'Para retirar', icon: InboxArrowDownIcon },
    { key: 'anteojos-entregados', label: 'Entregados', icon: ArchiveBoxIcon },
  ];
  const anteojosEstadosVisibles = hasModulo('estado_anteojos') ? anteojosEstados : [];
  // Sección Tienda Online / E-commerce
  const ecommerceItems = [
    { key: 'vendedor-ecommerce', label: 'Publicaciones y Catálogo', icon: TagIcon, modulo: 'tienda_online', accion: 'view' },
    { key: 'gestion-envios', label: 'Gestión de Envíos', icon: TruckIcon, modulo: 'tienda_online', accion: 'view' },
    { key: 'mercadopago', label: 'Mercado Pago (Cobros)', icon: CreditCardIcon, modulo: 'tienda_online', accion: 'modal_mp' },
    { key: 'politicas-web', label: 'Políticas y Descuentos Web', icon: SparklesIcon, modulo: 'tienda_online', accion: 'modal_politica' },
    { key: 'diseno-tienda', label: 'Diseño y Colores de Tienda', icon: SwatchIcon, modulo: 'tienda_online', accion: 'modal_diseno' },
    ...(usuario?.optica_slug ? [{ key: 'ver-tienda', label: 'Ver Tienda Online ↗', icon: GlobeAltIcon, modulo: 'tienda_online', accion: 'external' }] : []),
  ];
  const ecommerceVisibles = ecommerceItems.filter((item) => hasModulo(item.modulo));

  // Sección Herramientas
  const herramientasItems = [
    ...(esAdminOGerente ? [{ key: 'estadisticas-ventas', label: 'Estadísticas Ventas', icon: ChartBarIcon, modulo: 'estadisticas_ventas' }] : []),
    { key: 'gestion-notificaciones', label: 'Gestión Notificaciones', icon: BellIcon, modulo: 'dashboard' },
    { key: 'agregar-cristales', label: 'Cristales', icon: BeakerIcon, modulo: 'herramientas_cristales' },
    { key: 'stock-armazones', label: 'Stock Armazones', icon: EyeIcon, modulo: 'herramientas_stock_armazones' },
    { key: 'mensajeria', label: 'Mensajería', icon: ChatBubbleLeftRightIcon, modulo: 'mensajeria' },
    { key: 'presupuestos', label: 'Presupuestos', icon: DocumentTextIcon, modulo: 'presupuestos' },
  ];
  const herramientasVisibles = herramientasItems.filter((item) => hasModulo(item.modulo));

  const [dropdownAnteojosOpen, setDropdownAnteojosOpen] = React.useState(false);
  const [dropdownEcommerceOpen, setDropdownEcommerceOpen] = React.useState(false);
  const [dropdownHerramientasOpen, setDropdownHerramientasOpen] = React.useState(false);
  const [dropdownFinanzasOpen, setDropdownFinanzasOpen] = React.useState(false);
  const [dropdownUsuarioOpen, setDropdownUsuarioOpen] = React.useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = React.useState(false);
  const [mpModalOpen, setMpModalOpen] = React.useState(false);
  const [modalPoliticaOpen, setModalPoliticaOpen] = React.useState(false);
  const [modalDisenoOpen, setModalDisenoOpen] = React.useState(false);
  const [notificaciones, setNotificaciones] = React.useState([]);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const closeTimerNotif = React.useRef(null);

  const notifSinLeer = notificaciones.filter((n) => !n.leida).length;

  const cargarNotificaciones = React.useCallback(async () => {
    try {
      const [sisRes, pagosRes] = await Promise.allSettled([
        obtenerNotificaciones(false),
        obtenerNotificacionesPagos(false, 50)
      ]);

      const esSuperAdmin = usuario?.rol === 'superadmin';
      const sistemaNotifs = ((sisRes.status === 'fulfilled' && Array.isArray(sisRes.value)) ? sisRes.value : [])
        .filter((s) => esSuperAdmin || s.tipo !== 'nuevo_usuario');
      const pagosNotifs = (pagosRes.status === 'fulfilled' && Array.isArray(pagosRes.value))
        ? pagosRes.value.map((p) => ({ ...p, _esPagoDinamico: true, tipo: 'pago_dinamico' }))
        : [];

      // Evitar duplicados entre notificaciones guardadas en BD y notificaciones dinámicas de egresos
      const egresoIdsEnSistema = new Set(
        sistemaNotifs.filter((s) => s.datos?.egreso_id).map((s) => String(s.datos.egreso_id))
      );
      const pagosFiltrados = pagosNotifs.filter((p) => !egresoIdsEnSistema.has(String(p.id)));

      setNotificaciones([...sistemaNotifs, ...pagosFiltrados]);
    } catch (_e) {
      // silencioso: no interrumpir el nav si falla
    }
  }, [usuario]);

  React.useEffect(() => {
    if (!usuario) return;
    cargarNotificaciones();
    const interval = setInterval(cargarNotificaciones, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [usuario, cargarNotificaciones]);

  const handleMarcarLeida = async (target) => {
    try {
      const isObj = typeof target === 'object' && target !== null;
      const notifId = isObj ? target.id : target;

      if (!notifId || notifId === 'undefined') return;

      if (isObj && target._esPagoDinamico) {
        await marcarNotificacionPagoLeida(notifId);
      } else {
        await marcarNotificacionLeida(notifId);
      }

      setNotificaciones((prev) =>
        prev.map((item) => (item.id === notifId ? { ...item, leida: true } : item))
      );
    } catch (_e) { /* silencioso */ }
  };

  const handleMouseLeaveNotif = () => {
    closeTimerNotif.current = setTimeout(() => setNotifOpen(false), 300);
  };
  const handleMouseEnterNotif = () => clearTimeout(closeTimerNotif.current);

  const closeTimerAnteojos = React.useRef(null);
  const closeTimerEcommerce = React.useRef(null);
  const closeTimerHerramientas = React.useRef(null);
  const closeTimerFinanzas = React.useRef(null);
  const closeTimerUsuario = React.useRef(null);

  const DROPDOWN_CLOSE_DELAY = 200;

  const handleMouseLeaveAnteojos = () => {
    closeTimerAnteojos.current = setTimeout(() => setDropdownAnteojosOpen(false), DROPDOWN_CLOSE_DELAY);
  };
  const handleMouseEnterAnteojos = () => {
    clearTimeout(closeTimerAnteojos.current);
  };

  const handleMouseLeaveEcommerce = () => {
    closeTimerEcommerce.current = setTimeout(() => setDropdownEcommerceOpen(false), DROPDOWN_CLOSE_DELAY);
  };
  const handleMouseEnterEcommerce = () => {
    clearTimeout(closeTimerEcommerce.current);
  };

  const handleMouseLeaveHerramientas = () => {
    closeTimerHerramientas.current = setTimeout(() => setDropdownHerramientasOpen(false), DROPDOWN_CLOSE_DELAY);
  };
  const handleMouseEnterHerramientas = () => {
    clearTimeout(closeTimerHerramientas.current);
  };

  const handleMouseLeaveFinanzas = () => {
    closeTimerFinanzas.current = setTimeout(() => setDropdownFinanzasOpen(false), DROPDOWN_CLOSE_DELAY);
  };
  const handleMouseEnterFinanzas = () => {
    clearTimeout(closeTimerFinanzas.current);
  };

  const handleMouseLeaveUsuario = () => {
    closeTimerUsuario.current = setTimeout(() => setDropdownUsuarioOpen(false), DROPDOWN_CLOSE_DELAY);
  };
  const handleMouseEnterUsuario = () => {
    clearTimeout(closeTimerUsuario.current);
  };

  const handleLogout = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: '¿Deseas cerrar sesión?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        onLogout();
        Swal.fire({
          icon: 'success',
          title: 'Sesión cerrada',
          text: 'Hasta luego',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  return (
    <>
      <nav className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-[999] w-full">
        <div className="max-w-[1700px] w-full mx-auto px-4 sm:px-6">
          <div className="flex justify-between min-h-16 py-1">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div
                  style={{
                    ...logoFrameStyle,
                    transition: 'width 220ms ease, height 220ms ease',
                  }}
                  onClick={() => onViewChange('dashboard')}
                  title="Ir al Dashboard"
                  className="flex items-center justify-center overflow-hidden rounded-md cursor-pointer"
                >
                  {logoConfig.src ? (
                    <img
                      src={logoConfig.src}
                      alt={logoConfig.alt}
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                          setLogoAspectRatio(img.naturalWidth / img.naturalHeight);
                        }
                      }}
                      onError={() => setLogoAspectRatio(1)}
                      className="max-h-full max-w-full w-auto h-full object-contain object-center transition-all duration-200 ease-out"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-sm select-none">
                      {obtenerInicialesOptica(logoConfig.nombre)}
                    </div>
                  )}
                </div>
              </div>
              <div className="hidden sm:ml-4 sm:flex items-center gap-2 md:gap-3 lg:gap-4 xl:gap-5">
                {navItemsVisibles.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => onViewChange(item.key)}
                    className={`${currentView === item.key
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center`}
                  >
                    <item.icon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                    {item.label}
                  </button>
                ))}

                {/* Dropdown Finanzas */}
                {!esOperario && finanzasItemsVisibles.length > 0 && (
                  <div className="relative" onMouseLeave={handleMouseLeaveFinanzas} onMouseEnter={handleMouseEnterFinanzas}>
                    <button
                      onClick={() => setDropdownFinanzasOpen((open) => !open)}
                      className={`${finanzasItemsVisibles.some((f) => f.key === currentView)
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                      whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center`}
                      type="button"
                    >
                      <BanknotesIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                      Finanzas
                      <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
                      </svg>
                    </button>
                    {dropdownFinanzasOpen && (
                      <div className="origin-top-left absolute left-0 mt-2 w-44 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {finanzasItemsVisibles.map((item) => (
                            <button
                              key={item.key}
                              onClick={() => {
                                setDropdownFinanzasOpen(false);
                                onViewChange(item.key);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 ${currentView === item.key ? 'font-bold text-blue-600' : 'text-gray-700'}`}
                            >
                              <item.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Dropdown para Estado de anteojos */}
                {anteojosEstadosVisibles.length > 0 && (
                  <div className="relative" onMouseLeave={handleMouseLeaveAnteojos} onMouseEnter={handleMouseEnterAnteojos}>
                    <button
                      onClick={() => setDropdownAnteojosOpen((open) => !open)}
                      className={`${anteojosEstadosVisibles.some(e => e.key === currentView)
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center`}
                      type="button"
                    >
                      <EyeIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                      Estado de anteojos
                      <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
                      </svg>
                    </button>
                    {dropdownAnteojosOpen && (
                      <div className="origin-top-left absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {anteojosEstadosVisibles.map((estado) => (
                            <button
                              key={estado.key}
                              onClick={() => {
                                setDropdownAnteojosOpen(false);
                                onViewChange(estado.key);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 ${currentView === estado.key ? 'font-bold text-blue-600' : 'text-gray-700'}`}
                            >
                              <estado.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                              {estado.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Herramientas */}
                {herramientasVisibles.length > 0 && (
                  <div className="relative" onMouseLeave={handleMouseLeaveHerramientas} onMouseEnter={handleMouseEnterHerramientas}>
                    <button
                      onClick={() => setDropdownHerramientasOpen((open) => !open)}
                      className={`${herramientasVisibles.some(h => h.key === currentView)
                        ? 'border-blue-500 text-gray-900 font-bold'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center`}
                      type="button"
                    >
                      <WrenchScrewdriverIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                      Herramientas
                      <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
                      </svg>
                    </button>
                    {dropdownHerramientasOpen && (
                      <div className="origin-top-left absolute left-0 mt-2 w-48 rounded-xl shadow-xl bg-white border border-slate-100 ring-1 ring-black ring-opacity-5 z-50 overflow-hidden">
                        <div className="py-1">
                          {herramientasVisibles.map((item) => (
                            <button
                              key={item.key}
                              onClick={() => {
                                setDropdownHerramientasOpen(false);
                                setTimeout(() => onViewChange(item.key), 0);
                              }}
                              className={`w-full text-left px-4 py-2 text-xs flex items-center gap-2 hover:bg-slate-50 transition-colors ${currentView === item.key ? 'font-bold text-blue-600' : 'text-slate-700'}`}
                            >
                              <item.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tienda Online / E-commerce */}
                {ecommerceVisibles.length > 0 && (
                  <div className="relative" onMouseLeave={handleMouseLeaveEcommerce} onMouseEnter={handleMouseEnterEcommerce}>
                    <button
                      onClick={() => setDropdownEcommerceOpen((open) => !open)}
                      className={`${['vendedor-ecommerce', 'gestion-envios'].includes(currentView)
                        ? 'border-blue-500 text-gray-900 font-bold'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center`}
                      type="button"
                    >
                      <BuildingStorefrontIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-cyan-600" />
                      Tienda Online
                      <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
                      </svg>
                    </button>
                    {dropdownEcommerceOpen && (
                      <div className="origin-top-left absolute left-0 mt-2 w-60 rounded-2xl shadow-xl bg-white border border-slate-100 ring-1 ring-black ring-opacity-5 z-50 overflow-hidden">
                        <div className="py-1">
                          {ecommerceVisibles.map((item) => (
                            <button
                              key={item.key}
                              onClick={() => {
                                setDropdownEcommerceOpen(false);
                                if (item.accion === 'view') {
                                  setTimeout(() => onViewChange(item.key), 0);
                                } else if (item.accion === 'modal_mp') {
                                  setMpModalOpen(true);
                                } else if (item.accion === 'modal_politica') {
                                  setModalPoliticaOpen(true);
                                } else if (item.accion === 'modal_diseno') {
                                  setModalDisenoOpen(true);
                                } else if (item.accion === 'external') {
                                  const storeBaseUrl = process.env.REACT_APP_STOREFRONT_URL || 'http://localhost:3002';
                                  window.open(`${storeBaseUrl}/${usuario.optica_slug}`, '_blank');
                                }
                              }}
                              className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2.5 hover:bg-slate-50 transition-colors ${
                                currentView === item.key ? 'font-bold text-cyan-600 bg-cyan-50/50' : 'text-slate-700'
                              }`}
                            >
                              <item.icon className={`h-4 w-4 flex-shrink-0 ${currentView === item.key ? 'text-cyan-600' : 'text-slate-400'}`} />
                              <span>{item.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
            <div className="flex items-center gap-3 ml-auto pl-2 lg:pl-4 flex-shrink-0">
              {/* Campanita de notificaciones */}
              {usuario && (
                <div
                  className="relative"
                  onMouseLeave={handleMouseLeaveNotif}
                  onMouseEnter={handleMouseEnterNotif}
                >
                  <button
                    onClick={() => setNotifOpen((o) => !o)}
                    className="relative p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    aria-label="Notificaciones"
                  >
                    <BellIcon className="h-5 w-5" />
                    {notifSinLeer > 0 && (
                      <span className="absolute top-0.5 right-0.5 flex items-center justify-center h-4 w-4 text-[10px] font-bold bg-red-500 text-white rounded-full">
                        {notifSinLeer > 9 ? '9+' : notifSinLeer}
                      </span>
                    )}
                  </button>

                  {notifOpen && (
                    <div className="fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[1001]">
                      <div className="flex items-center justify-between px-4 py-2 border-b">
                        <span className="font-semibold text-sm text-gray-800">Notificaciones</span>
                        {notifSinLeer > 0 && (
                          <button
                            onClick={async () => {
                              await Promise.all(
                                notificaciones.filter((n) => !n.leida).map((n) => handleMarcarLeida(n))
                              );
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Marcar todas leídas
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notificaciones.length === 0 ? (
                          <div className="px-4 py-6 text-center text-sm text-gray-500">Sin notificaciones</div>
                        ) : (
                          notificaciones.slice(0, 20).map((n, idx) => {
                            const esLab =
                              n.tipo === 'laboratorio_alertas' ||
                              n.tipo?.includes('laboratorio') ||
                              n.titulo?.toLowerCase().includes('laboratorio') ||
                              n.mensaje?.toLowerCase().includes('laboratorio');

                            const esPago =
                              n.tipo === 'pagos_alerta' ||
                              n.tipo === 'pago_dinamico' ||
                              n.tipo?.includes('pago') ||
                              n.titulo?.toLowerCase().includes('pago');

                            const esNuevoUsuario =
                              n.tipo === 'nuevo_usuario' ||
                              n.titulo?.toLowerCase().includes('nuevo usuario');

                            const datos = n.datos || {};
                            const itemKey = n.id ? `${n.tipo || 'notif'}-${n.id}` : `notif-${n.tipo || 'item'}-${idx}`;

                            return (
                              <div
                                key={itemKey}
                                className={`px-4 py-3 border-b last:border-b-0 flex items-start gap-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${n.leida ? 'opacity-60' : esLab ? 'bg-amber-50/70' : esNuevoUsuario ? 'bg-purple-50/70' : 'bg-blue-50/70'
                                  }`}
                                onClick={() => {
                                  handleMarcarLeida(n);
                                  setNotifOpen(false);
                                  if (esNuevoUsuario) {
                                    const usuarioId = datos.usuario_id;
                                    const nombreUsuario = datos.nombre_completo || 'Usuario';
                                    const emailUsuario = datos.email || '';
                                    const opticaId = datos.optica_id;

                                    Swal.fire({
                                      title: 'Nuevo usuario registrado',
                                      html: `
                                      <div style="text-align: left; font-size: 14px; line-height: 1.6;">
                                        <p><strong>Nombre:</strong> ${nombreUsuario}</p>
                                        <p><strong>Email:</strong> ${emailUsuario}</p>
                                        <p style="margin-top: 10px; color: #4b5563;">
                                          ¿Deseás activar a este usuario para darle permiso de utilizar la plataforma?
                                        </p>
                                      </div>
                                    `,
                                      icon: 'question',
                                      showCancelButton: true,
                                      showDenyButton: true,
                                      confirmButtonText: 'Sí, activar usuario',
                                      denyButtonText: 'Ir a panel de usuarios',
                                      cancelButtonText: 'Cerrar',
                                      confirmButtonColor: '#10b981',
                                      denyButtonColor: '#6366f1',
                                    }).then(async (result) => {
                                      if (result.isConfirmed) {
                                        try {
                                          await activarUsuario(usuarioId, opticaId);
                                          Swal.fire({
                                            icon: 'success',
                                            title: '¡Usuario activado!',
                                            text: `El usuario ${nombreUsuario} ahora puede iniciar sesión en la plataforma.`,
                                            timer: 2500,
                                            showConfirmButton: false,
                                          });
                                        } catch (err) {
                                          Swal.fire({
                                            icon: 'error',
                                            title: 'Error al activar',
                                            text: err.message || 'No se pudo activar el usuario',
                                          });
                                        }
                                      } else if (result.isDenied) {
                                        if (usuario?.rol === 'superadmin') {
                                          onViewChange('superadmin');
                                        } else {
                                          setAdminPanelOpen(true);
                                        }
                                      }
                                    });
                                  } else if (esLab) {
                                    onViewChange('anteojos-laboratorio');
                                  } else {
                                    onViewChange('finanzas');
                                  }
                                }}
                              >
                                {esLab ? (
                                  <BeakerIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                ) : esNuevoUsuario ? (
                                  <UserPlusIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <BellIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  {esLab && (
                                    <div>
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-200 text-amber-900">
                                          Laboratorio
                                        </span>
                                        {datos.dias_laboratorio && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                                            {datos.dias_laboratorio} días
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs font-semibold text-gray-900 leading-snug">
                                        {n.mensaje || (datos.tipo_anteojo ? `${datos.tipo_anteojo} para ${datos.cliente_nombre || ''}` : n.titulo || 'Consulta de laboratorio')}
                                      </p>
                                      {(datos.nro_sobre || datos.cliente_telefono) && (
                                        <p className="text-[11px] text-gray-600 mt-0.5">
                                          {datos.nro_sobre ? `Sobre #${datos.nro_sobre}` : ''}
                                          {datos.nro_sobre && datos.cliente_telefono ? ' • ' : ''}
                                          {datos.cliente_telefono ? `Tel: ${datos.cliente_telefono}` : ''}
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {esPago && (
                                    <div>
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-200 text-blue-900">
                                          Pago Pendiente
                                        </span>
                                      </div>
                                      <p className="text-xs font-medium text-gray-900 leading-snug">
                                        {n.mensaje || n.descripcion || (datos.tipo ? `${datos.tipo} - ${datos.receptor || ''} - $${datos.importe || ''}` : n.titulo)}
                                      </p>
                                    </div>
                                  )}

                                  {esNuevoUsuario && (
                                    <div>
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-200 text-purple-900">
                                          Nuevo Usuario
                                        </span>
                                      </div>
                                      <p className="text-xs font-semibold text-gray-900 leading-snug">
                                        {n.titulo || 'Nuevo usuario registrado'}
                                      </p>
                                      <p className="text-xs text-gray-600 mt-0.5">
                                        {n.mensaje}
                                      </p>
                                    </div>
                                  )}

                                  {!esLab && !esPago && !esNuevoUsuario && (
                                    <p className="text-xs font-medium text-gray-900 leading-snug">
                                      {n.mensaje || n.titulo || n.descripcion || (n.tipo ? `Notificación de ${n.tipo}` : 'Notificación del sistema')}
                                    </p>
                                  )}

                                  {n.fecha_creacion && (
                                    <p className="text-[10px] text-gray-400 mt-1">
                                      {new Date(n.fecha_creacion).toLocaleDateString('es-AR')}
                                    </p>
                                  )}
                                  {n.fecha_cobro && !n.fecha_creacion && (
                                    <p className="text-[10px] text-gray-400 mt-1">
                                      Vence: {new Date(n.fecha_cobro).toLocaleDateString('es-AR')}
                                    </p>
                                  )}
                                </div>
                                {!n.leida && <span className="mt-1 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="px-4 py-2 border-t flex items-center justify-between gap-2 bg-gray-50 text-xs">
                        <button
                          onClick={() => { setNotifOpen(false); onViewChange('gestion-notificaciones'); }}
                          className="text-blue-600 hover:underline font-semibold"
                        >
                          Administrar e historial
                        </button>
                        <button
                          onClick={() => { setNotifOpen(false); onViewChange('finanzas'); }}
                          className="text-gray-600 hover:underline"
                        >
                          Calendario de pagos
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {usuario && isDemoUser(usuario) && (
                <div className="flex items-center gap-2 mr-1">
                  <button
                    type="button"
                    onClick={abrirTourDemo}
                    className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl transition-all cursor-pointer shadow-2xs"
                    title="Ver tour interactivo de los módulos"
                  >
                    <SparklesIcon className="w-4 h-4 text-indigo-600" />
                    <span>Tour de Módulos</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => solicitarRegistroDemo('activar tu propia óptica con 3 meses gratis')}
                    className="hidden md:inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-black text-white bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-500 hover:to-indigo-500 rounded-xl shadow-md shadow-cyan-600/20 transition-all hover:scale-105 active:scale-95 cursor-pointer"
                    title="Crear tu propia óptica con 3 meses bonificados"
                  >
                    <RocketLaunchIcon className="w-4 h-4 text-cyan-200" />
                    <span className="hidden sm:inline">Activar mi Óptica (3 Meses Gratis)</span>
                    <span className="sm:hidden">Activar Óptica</span>
                  </button>
                </div>
              )}

              {usuario && (
                <div className="relative" onMouseLeave={handleMouseLeaveUsuario} onMouseEnter={handleMouseEnterUsuario}>
                  <button
                    onClick={() => setDropdownUsuarioOpen(!dropdownUsuarioOpen)}
                    className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-200/80 sm:border-transparent transition-colors cursor-pointer"
                    title={usuario.nombre_completo}
                  >
                    <UserCircleIcon className="w-5 h-5 text-gray-500 shrink-0" />
                    <span className="max-w-[70px] sm:max-w-[120px] md:max-w-[160px] truncate font-medium">
                      {usuario.nombre_completo}
                    </span>
                    <span className={`inline-block px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-md font-semibold shrink-0 ${getRolBadgeClass(usuario.rol)}`}>
                      {formatRolLabel(usuario.rol)}
                    </span>
                    <ChevronDownIcon className={`h-3.5 w-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${dropdownUsuarioOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {dropdownUsuarioOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-56 max-w-[calc(100vw-24px)] rounded-2xl shadow-2xl bg-white border border-gray-100 ring-1 ring-black/5 z-[1000] overflow-hidden animate-fadeIn">
                      <div className="p-3 bg-slate-50 border-b border-gray-100">
                        <div className="font-bold text-sm text-gray-900 truncate" title={usuario.nombre_completo}>
                          {usuario.nombre_completo}
                        </div>
                        <div className="text-gray-500 text-xs truncate mt-0.5" title={usuario.email}>
                          {usuario.email}
                        </div>
                      </div>
                      <div className="py-1">
                        {esSuperAdmin && (
                          <button
                            onClick={() => {
                              setDropdownUsuarioOpen(false);
                              onViewChange('superadmin');
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 border-b border-gray-100 transition-colors cursor-pointer"
                          >
                            <BuildingOfficeIcon className="h-4 w-4 text-gray-500 shrink-0" />
                            <span>Super Admin</span>
                          </button>
                        )}
                        {(usuario.rol === 'admin' || usuario.rol === 'superadmin') && (
                          <button
                            onClick={() => {
                              setDropdownUsuarioOpen(false);
                              setAdminPanelOpen(true);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 border-b border-gray-100 transition-colors cursor-pointer"
                          >
                            <Cog6ToothIcon className="h-4 w-4 text-gray-500 shrink-0" />
                            <span>Gestionar Usuarios</span>
                          </button>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors font-medium cursor-pointer"
                        >
                          <ArrowLeftStartOnRectangleIcon className="h-4 w-4 text-red-500 shrink-0" />
                          <span>Cerrar sesión</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      {adminPanelOpen && (
        <AdminPanel
          usuario={usuario}
          onClose={() => setAdminPanelOpen(false)}
        />
      )}
      <MercadoPagoConfigModal
        isOpen={mpModalOpen}
        onClose={() => setMpModalOpen(false)}
      />
      <ModalPoliticaDescuentos
        isOpen={modalPoliticaOpen}
        onClose={() => setModalPoliticaOpen(false)}
      />
      <TiendaDisenoModal
        isOpen={modalDisenoOpen}
        onClose={() => setModalDisenoOpen(false)}
      />
    </>
  );
};

const usuarioShape = PropTypes.shape({
  nombre_completo: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  rol: PropTypes.string.isRequired,
  optica_id: PropTypes.number,
});

Navigation.propTypes = {
  currentView: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
  usuario: usuarioShape,
  modulosHabilitados: PropTypes.arrayOf(PropTypes.string),
  onLogout: PropTypes.func.isRequired,
};

export default Navigation;
